import { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import {
  Search, Library, ExternalLink, Star, X, Send, Sparkles, ArrowRight,
  Globe, FlaskConical, TrendingUp, Palette, Server, DollarSign, BarChart3,
  Blocks, CheckSquare, Mail, Shield, Users, Rocket, Headphones, Code2, Megaphone, Layout as LayoutIcon, Briefcase,
} from "lucide-react";
import { BuilderLogo } from "./BuilderLogo";
import { resourcesLibrary, resourcesRequest, resourcesFavorites, resourcesToggleFavorite } from "../lib/api";
import { useAuth } from "../hooks/useAuth";

const ICONS = { google: Globe, sparkles: Sparkles, flask: FlaskConical, trending: TrendingUp, megaphone: Megaphone, briefcase: Briefcase, layout: LayoutIcon, palette: Palette, server: Server, dollar: DollarSign, chart: BarChart3, blocks: Blocks, check: CheckSquare, mail: Mail, shield: Shield, users: Users, rocket: Rocket, headset: Headphones, code: Code2 };

const CAT_COLORS = {
  "google-tools": "#4285F4", "ai-builders": "#7c3aed", "app-testing": "#0d9488",
  "marketing": "#ea580c", "influencer-marketing": "#E1306C", "business-formation": "#B8901F", "landing-page": "#0ea5a4", "design": "#db2777",
  "domains-hosting": "#2563eb", "payments": "#16a34a", "analytics": "#0891b2",
  "no-code": "#f59e0b", "productivity": "#4f46e5", "email-crm": "#0ea5e9",
  "legal": "#64748b", "communities": "#8b5cf6", "launch": "#f43f5e",
  "support": "#14b8a6", "developer": "#6366f1",
};

const ResourceCard = ({ t, saved, onFav, catLabel }) => {
  const [imgError, setImgError] = useState(false);
  const showPreview = t.id !== "replit-lp" && !imgError;
  return (
    <div className="rounded-2xl border border-gray-100 bg-white p-5 hover:shadow-[0_8px_24px_rgb(0,0,0,0.05)] transition-shadow" data-testid={`resource-card-${t.id}`}>
      {showPreview && (
        <a href={t.url} target="_blank" rel="noopener noreferrer" className="block relative rounded-xl overflow-hidden border border-gray-100 mb-4 aspect-[16/10] bg-gray-50 group" data-testid={`resource-preview-${t.id}`}>
          <img
            src={`https://s.wordpress.com/mshots/v1/${encodeURIComponent(t.url)}?w=640&h=400`}
            alt={`${t.name} website preview`}
            loading="lazy"
            onError={() => setImgError(true)}
            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
          />
          <span className="absolute bottom-2 right-2 inline-flex items-center gap-1 text-[10px] font-bold text-white bg-black/60 backdrop-blur-sm rounded-full px-2.5 py-1 opacity-0 group-hover:opacity-100 transition-opacity">Visit Website <ExternalLink size={10} /></span>
        </a>
      )}
      <div className="flex items-start justify-between gap-2">
        <BuilderLogo url={t.url} name={t.name} size={40} />
        <button onClick={() => onFav(t.id)} className="text-[#9aa1ab] hover:text-[#D4AF37]" data-testid={`resource-fav-${t.id}`}>
          <Star size={18} className={saved ? "text-[#D4AF37] fill-[#D4AF37]" : ""} />
        </button>
      </div>
      <p className="font-display font-bold text-[#0F0F0F] mt-3">{t.name}</p>
      <p className="text-sm text-[#687280] mt-1 min-h-[40px]">{t.desc}</p>
      <div className="flex items-center justify-between mt-3">
        <span className="text-[11px] font-semibold text-[#8a6d1a] bg-[#FBF7EA] rounded-full px-2.5 py-1">{catLabel}</span>
        <a href={t.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs font-bold text-[#0F0F0F] hover:text-[#8a6d1a]" data-testid={`resource-open-${t.id}`}>Open Website <ExternalLink size={12} /></a>
      </div>
    </div>
  );
};

export const FounderResourceLibrary = () => {
  const { user, requireAuth } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [lib, setLib] = useState(null);
  const [query, setQuery] = useState("");
  const [activeCat, setActiveCat] = useState("developer");
  const [favIds, setFavIds] = useState([]);
  const [showRequest, setShowRequest] = useState(false);
  const [reqName, setReqName] = useState("");
  const [reqNote, setReqNote] = useState("");

  useEffect(() => { resourcesLibrary().then(setLib).catch(() => {}); }, []);
  useEffect(() => {
    if (!lib) return;
    const cat = new URLSearchParams(location.search).get("resources");
    if (cat && lib.categories.some((c) => c.slug === cat)) {
      setActiveCat(cat);
      setTimeout(() => document.getElementById("resource-library")?.scrollIntoView({ behavior: "smooth", block: "start" }), 400);
    }
  }, [lib, location.search]);
  const loadFavs = useCallback(() => { if (user) resourcesFavorites().then((d) => setFavIds(d.ids || [])).catch(() => {}); else setFavIds([]); }, [user]);
  useEffect(() => { loadFavs(); }, [loadFavs]);

  const catName = useCallback((slug) => lib?.categories.find((c) => c.slug === slug)?.name || slug, [lib]);

  const searchResults = useMemo(() => {
    if (!lib || !query.trim()) return [];
    const q = query.toLowerCase();
    return lib.tools.filter((t) => t.name.toLowerCase().includes(q) || catName(t.category).toLowerCase().includes(q) || t.desc.toLowerCase().includes(q)).slice(0, 6);
  }, [lib, query, catName]);

  const externalUrl = useMemo(() => {
    const q = query.trim();
    if (!q) return "";
    const looksDomain = /^[a-z0-9.-]+\.[a-z]{2,}$/i.test(q) && !q.includes(" ");
    return looksDomain ? `https://${q.replace(/^https?:\/\//, "")}` : `https://www.google.com/search?q=${encodeURIComponent(q)}`;
  }, [query]);

  const shown = useMemo(() => {
    if (!lib) return [];
    if (query.trim()) {
      const q = query.toLowerCase();
      return lib.tools.filter((t) => t.name.toLowerCase().includes(q) || catName(t.category).toLowerCase().includes(q) || t.desc.toLowerCase().includes(q));
    }
    return lib.tools.filter((t) => t.category === activeCat);
  }, [lib, activeCat, query, catName]);

  const toggleFav = (id) => {
    const on = !favIds.includes(id);
    setFavIds((p) => on ? [...p, id] : p.filter((x) => x !== id));
    const run = async () => { try { const d = await resourcesToggleFavorite(id, on); setFavIds(d.ids || []); if (on) toast.success("Saved to your favorites"); } catch { loadFavs(); } };
    user ? run() : requireAuth(run);
  };

  const submitRequest = async () => {
    if (!reqName.trim()) { toast.error("Enter a tool name"); return; }
    try { await resourcesRequest({ name: reqName.trim(), note: reqNote.trim(), email: user?.email }); toast.success("Thanks! We'll consider adding it."); setShowRequest(false); setReqName(""); setReqNote(""); }
    catch { toast.error("Could not send request"); }
  };

  if (!lib) return null;

  return (
    <section id="resource-library" className="max-w-7xl mx-auto px-6 lg:px-8 pt-16" data-testid="resource-library">
      <div className="text-center max-w-2xl mx-auto">
        <span className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-widest text-[#B8901F] bg-[#FBF7EA] border border-[#D4AF37]/30 rounded-full px-3 py-1"><Library size={13} /> Founder Resource Library</span>
        <h2 className="font-display text-3xl sm:text-4xl font-extrabold text-[#0F0F0F] mt-4">Every Tool You Need, In One Place</h2>
        <p className="text-[#687280] mt-3">Discover the best tools, platforms and services to build, launch and grow your startup.</p>
      </div>

      {/* Search */}
      <div className="relative max-w-2xl mx-auto mt-7">
        <Search size={18} className="absolute left-5 top-1/2 -translate-y-1/2 text-[#9aa1ab]" />
        <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search founder resources, tools, platforms..." className="w-full rounded-full border border-gray-200 pl-12 pr-5 py-4 text-sm focus:outline-none focus:border-[#D4AF37] shadow-[0_4px_20px_rgb(0,0,0,0.04)]" data-testid="resource-search" />
        {query.trim() && (
          <div className="absolute z-20 left-0 right-0 mt-2 bg-white rounded-2xl border border-gray-100 shadow-xl overflow-hidden" data-testid="resource-search-dropdown">
            {searchResults.map((t) => (
              <a key={t.id} href={t.url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 border-b border-gray-50" data-testid={`resource-search-item-${t.id}`}>
                <BuilderLogo url={t.url} name={t.name} size={30} />
                <div className="min-w-0 flex-1"><p className="font-semibold text-sm text-[#0F0F0F]">{t.name}</p><p className="text-xs text-[#9aa1ab] truncate">{catName(t.category)} · {t.desc}</p></div>
                <ExternalLink size={14} className="text-[#9aa1ab]" />
              </a>
            ))}
            <a href={externalUrl} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 px-4 py-3 hover:bg-[#FBF7EA] bg-[#FBF7EA]/40" data-testid="resource-search-external">
              <span className="w-[30px] h-[30px] rounded-lg bg-[#0F0F0F] text-white flex items-center justify-center shrink-0"><Globe size={16} /></span>
              <div className="min-w-0 flex-1">
                <p className="font-semibold text-sm text-[#0F0F0F]">{searchResults.length ? "Not what you need?" : `Search the web for "${query}"`}</p>
                <p className="text-xs text-[#9aa1ab] truncate">Look up “{query}” outside LaunchBS and visit the site</p>
              </div>
              <ArrowRight size={14} className="text-[#9aa1ab]" />
            </a>
          </div>
        )}
      </div>

      {/* Categories */}
      <div className="flex flex-wrap gap-2 justify-center mt-6" data-testid="resource-categories">
        {lib.categories.map((c) => {
          const Icon = ICONS[c.icon] || Library;
          const on = activeCat === c.slug;
          return (
            <button key={c.slug} onClick={() => (c.slug === "influencer-marketing" || c.slug === "business-formation") ? navigate(`/resources/${c.slug}`) : setActiveCat(c.slug)} className={`inline-flex items-center gap-1.5 rounded-full px-3.5 py-2 text-xs font-semibold border transition-colors ${on ? "bg-[#D4AF37] text-[#0F0F0F] border-[#D4AF37]" : "bg-white text-[#374151] border-gray-200 hover:border-[#D4AF37]"}`} data-testid={`resource-cat-${c.slug}`}>
              <Icon size={14} style={on ? undefined : { color: CAT_COLORS[c.slug] }} /> {c.name} <span className="opacity-60">({c.count})</span>
            </button>
          );
        })}
      </div>

      {/* Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-7" data-testid="resource-cards">
        {shown.map((t) => (
          <ResourceCard key={t.id} t={t} saved={favIds.includes(t.id)} onFav={toggleFav} catLabel={catName(t.category)} />
        ))}
        {shown.length === 0 && (
          <div className="col-span-full text-center py-8" data-testid="resource-empty">
            <p className="text-sm text-[#9aa1ab]">No tools in the library match "{query}".</p>
            <a href={externalUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 mt-3 bg-[#0F0F0F] text-white rounded-full px-5 py-2.5 text-sm font-bold hover:bg-[#1c1c1c]" data-testid="resource-empty-external"><Globe size={15} /> Search the web for "{query}"</a>
          </div>
        )}
      </div>

      {/* Recently added + Featured */}
      <div className="grid lg:grid-cols-[1fr_340px] gap-6 mt-10">
        <div>
          <h3 className="font-display text-lg font-bold text-[#0F0F0F] mb-4">Recently Added</h3>
          <div className="flex flex-wrap gap-3" data-testid="resource-recent">
            {lib.recent.map((t) => (
              <a key={t.id} href={t.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 rounded-full border border-gray-200 bg-white px-3.5 py-2 hover:border-[#D4AF37] transition-colors" data-testid={`resource-recent-${t.id}`}>
                <BuilderLogo url={t.url} name={t.name} size={22} /> <span className="text-sm font-semibold text-[#0F0F0F]">{t.name}</span>
              </a>
            ))}
          </div>
          {/* Request a tool */}
          <div className="mt-8 rounded-2xl border border-dashed border-[#D4AF37]/50 bg-[#FBF7EA]/40 p-5 flex flex-col sm:flex-row items-center gap-4" data-testid="resource-request-card">
            <div className="flex-1 text-center sm:text-left">
              <p className="font-display font-bold text-[#0F0F0F]">Can't find a resource?</p>
              <p className="text-sm text-[#687280]">Suggest a tool and we'll consider adding it to the library.</p>
            </div>
            <button onClick={() => setShowRequest(true)} className="inline-flex items-center gap-2 bg-[#0F0F0F] text-white rounded-full px-5 py-2.5 text-sm font-bold hover:bg-[#1c1c1c] shrink-0" data-testid="resource-request-btn"><Send size={15} /> Request a Tool</button>
          </div>
        </div>

        {/* Featured */}
        {lib.featured && (
          <div className="rounded-3xl bg-gradient-to-br from-[#0F0F0F] to-[#23200f] text-white p-6 flex flex-col" data-testid="resource-featured">
            <p className="text-[11px] font-bold uppercase tracking-widest text-[#D4AF37]">Featured Resource</p>
            <div className="flex items-center gap-3 mt-3"><BuilderLogo url={lib.featured.url} name={lib.featured.name} size={44} /><p className="font-display text-2xl font-extrabold">{lib.featured.name}</p></div>
            <p className="text-sm text-white/70 mt-3 flex-1">{lib.featured.desc}</p>
            <div className="flex gap-0.5 mt-3">{[1, 2, 3, 4, 5].map((i) => <Star key={i} size={16} className="text-[#D4AF37] fill-[#D4AF37]" />)}</div>
            <a href={lib.featured.url} target="_blank" rel="noopener noreferrer" className="mt-4 inline-flex items-center justify-center gap-2 bg-[#D4AF37] text-[#0F0F0F] rounded-full py-3 text-sm font-bold hover:bg-[#c5a030]" data-testid="resource-featured-btn">Learn More <ArrowRight size={15} /></a>
          </div>
        )}
      </div>

      {/* Request modal */}
      {showRequest && (
        <div className="fixed inset-0 z-[95] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setShowRequest(false)} data-testid="resource-request-modal">
          <div className="bg-white rounded-3xl w-full max-w-md p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between"><p className="font-display text-xl font-extrabold text-[#0F0F0F]">Request a Tool</p><button onClick={() => setShowRequest(false)} className="text-[#687280] hover:text-[#0F0F0F]" data-testid="resource-request-close"><X size={20} /></button></div>
            <input value={reqName} onChange={(e) => setReqName(e.target.value)} placeholder="Tool name" className="w-full mt-4 rounded-xl border border-gray-200 px-4 py-3 text-sm focus:outline-none focus:border-[#D4AF37]" data-testid="resource-request-name" />
            <textarea value={reqNote} onChange={(e) => setReqNote(e.target.value)} placeholder="Why should we add it? (optional)" rows={3} className="w-full mt-3 rounded-xl border border-gray-200 px-4 py-3 text-sm focus:outline-none focus:border-[#D4AF37] resize-none" data-testid="resource-request-note" />
            <button onClick={submitRequest} className="mt-4 w-full bg-[#D4AF37] text-[#0F0F0F] rounded-full py-3 text-sm font-bold hover:bg-[#c5a030]" data-testid="resource-request-submit">Submit Request</button>
          </div>
        </div>
      )}
    </section>
  );
};
